/**
 * A table as CSV.
 *
 * Pure, so the escaping can be tested: a report that mangles one customer's name in a file nobody opens
 * until it matters is worse than no export at all.
 */

export interface Column<T> {
  /** The header text. */
  header: string;
  /** The cell, already a string. Formatting belongs to the report, not to the writer. */
  value: (row: T) => string;
}

/**
 * Values that make Excel and Sheets run a FORMULA instead of showing text.
 *
 * This report carries names and email addresses that customers typed, so a cell beginning `=` or `@` is
 * not hypothetical — it is user input reaching a spreadsheet that will execute it. The fix is the usual
 * one: a leading apostrophe, which Excel strips on display and never evaluates. Tab and carriage return
 * are in the list because they are stripped on paste and can expose the character after them.
 */
const FORMULA_LEAD = /^[=+\-@\t\r]/;

export function escapeCell(value: string): string {
  const safe = FORMULA_LEAD.test(value) ? `'${value}` : value;
  // Quote when the value carries a separator, a quote, a newline, or edge whitespace a reader would lose.
  if (/[",\r\n]/.test(safe) || safe !== safe.trim()) return `"${safe.replace(/"/g, '""')}"`;
  return safe;
}

/**
 * The whole file.
 *
 * CRLF line endings, which is what RFC 4180 says and what Excel on Windows expects. The byte-order mark
 * is not decoration: without it Excel reads a UTF-8 CSV as the system code page, and the first customer
 * with an accented name comes out as mojibake.
 */
export function toCsv<T>(rows: readonly T[], columns: readonly Column<T>[]): string {
  const lines = [
    columns.map((c) => escapeCell(c.header)).join(','),
    ...rows.map((r) => columns.map((c) => escapeCell(c.value(r))).join(',')),
  ];
  return `\uFEFF${lines.join('\r\n')}\r\n`;
}
